import Loading, { LoadingBall } from '@ingka/loading';
import Text from '@ingka/text';
import { FormBuilder, type FormBuilderConfig, useForm } from '@sp-saljapro/form-builder';

import type { DtoGetOrderQuestionnairesResponse } from '../generated';
import { buildQuestionnaireSectionFormConfigs } from './questionnaire.form';
import type { GetOrderQuestionnairesInput } from './questionnaire.types';

export interface QuestionnaireFormContentTranslations {
    readonly loading: string;
    readonly error: string;
    readonly empty: string;
    readonly deliveryQuestionnaireTitle: string;
    readonly serviceQuestionnaireTitle: string;
    readonly questionnaireTypeLabel: string;
    readonly requiredField: string;
    readonly freeTextLabel: string;
    readonly freeTextOptional: string;
    readonly freeTextRequired: string;
    readonly optionYes: string;
    readonly optionNo: string;
    readonly optionNotApplicable: string;
    readonly additionalInfoSp: string;
    readonly additionalInfoTsp: string;
    readonly delAddressInRestrictedTrafficZone: string;
    readonly delAddressPartTimeAccessible: string;
    readonly distanceToParking: string;
    readonly doorCode: string;
    readonly doorWideEnough: string;
    readonly electricityAvailable: string;
    readonly elevatorExists: string;
    readonly externalElevator: string;
    readonly floorAndWallsFinished: string;
    readonly floorNo: string;
    readonly furnitureToBeRemoved: string;
    readonly islandFerry: string;
    readonly kitchenChecklistSigned: string;
    readonly mergedWithOrder: string;
    readonly na: string;
    readonly nameOfDoorBell: string;
    readonly parkingAvailable: string;
    readonly parkingPermitNeeded: string;
    readonly roomPrepared: string;
    readonly selectorBusinessCustomer: string;
    readonly selectorFlat: string;
    readonly selectorHouse: string;
    readonly stairCaseCurved: string;
    readonly stairCaseSize: string;
    readonly stairCaseWideEnough: string;
    readonly transportConnected: string;
    readonly truckDeliveryRestrictions: string;
    readonly typeOfWhiteGoods: string;
    readonly yes: string;
}

interface QuestionnaireFormContentProps {
    readonly className?: string | undefined;
    readonly data: DtoGetOrderQuestionnairesResponse | undefined;
    readonly isLoading: boolean;
    readonly isError: boolean;
    readonly params: GetOrderQuestionnairesInput;
    readonly translations: QuestionnaireFormContentTranslations;
}

const hasElements = (config: FormBuilderConfig): boolean => config.elements.length > 0;

export const QuestionnaireFormContent = ({
    className,
    data,
    isLoading,
    isError,
    params,
    translations,
}: QuestionnaireFormContentProps) => {
    if (isLoading) {
        return (
            <Loading text={translations.loading}>
                <LoadingBall color="primary" />
            </Loading>
        );
    }

    if (isError || !data) {
        return (
            <Text tagName="p" textStyle="Body.M">
                {translations.error}
            </Text>
        );
    }

    return (
        <QuestionnaireForm
            className={className}
            data={data}
            resetKey={`${params.orderNoSource}-${params.orderNo}`}
            translations={translations}
        />
    );
};

const QuestionnaireForm = ({
    className,
    data,
    resetKey,
    translations,
}: {
    readonly className?: string | undefined;
    readonly data: DtoGetOrderQuestionnairesResponse;
    readonly resetKey: string;
    readonly translations: QuestionnaireFormContentTranslations;
}) => {
    const { deliveryConfig, serviceConfig } = buildQuestionnaireSectionFormConfigs({
        orderQuestionnaires: data,
        requiredFieldMessage: translations.requiredField,
        translations,
    });
    const { form: deliveryForm } = useForm({ config: deliveryConfig, resetKey: `${resetKey}-delivery` });
    const { form: serviceForm } = useForm({ config: serviceConfig, resetKey: `${resetKey}-service` });

    if (!hasElements(deliveryConfig) && !hasElements(serviceConfig)) {
        return (
            <Text tagName="p" textStyle="Body.M">
                {translations.empty}
            </Text>
        );
    }

    return (
        <div className={className}>
            {hasElements(deliveryConfig) && (
                <section>
                    <Text tagName="h3" textStyle="Heading.S">
                        {translations.deliveryQuestionnaireTitle}
                    </Text>
                    <hr />
                    <FormBuilder form={deliveryForm} className={className} />
                </section>
            )}
            {hasElements(serviceConfig) && (
                <section>
                    <Text tagName="h3" textStyle="Heading.S">
                        {translations.serviceQuestionnaireTitle}
                    </Text>
                    <hr />
                    <FormBuilder form={serviceForm} className={className} />
                </section>
            )}
        </div>
    );
};
